import { useEffect, useRef, useState } from 'react';
import propTypes from 'prop-types';
import { Form } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@kiper/ui';
import { Col, Title, Subtitle, ButtonSubmit, ButtonLink } from '../styles';
import {
  OtpRow,
  OtpCell,
  Countdown,
  CountdownDot,
  Feedback,
  HelpTitle,
  HelpBlock,
  HelpText,
  TutorialSteps,
  TutorialStep,
  StepText,
} from './styles';
import { useOtpInput, useChallengeCountdown } from './hooks';
import { LostAccessModal } from './modal';

const OTP_LENGTH = 6;
// Abaixo de 30s: destaca o contador.
const WARN_THRESHOLD = 30;
const HELP_STEPS = [1, 2, 3, 4];
// Ajuda de data/hora só depois de errar duas vezes.
const MIN_INVALID_ATTEMPTS = 2;

const LoginMfaChallenge = ({
  errors,
  submitCount,
  isSubmitting,
  handleSubmit,
  setFieldValue,
  setCurrentStep,
  steps,
}) => {
  const [t] = useTranslation('login');
  // Só o erro vindo do backend (setFieldError('code','invalid')) exibe "código
  // incorreto". Erros do Yup (length/required) ao digitar/reentrar NÃO contam.
  const hasError = !!submitCount && errors.code === 'invalid';

  const [lostAccessOpen, setLostAccessOpen] = useState(false);
  const toggleLostAccess = () => setLostAccessOpen(open => !open);

  const [invalidCodeAttempts, setInvalidCodeAttempts] = useState(0);
  const lastCountedSubmit = useRef(0);
  const showCodeHelp = hasError && invalidCodeAttempts >= MIN_INVALID_ATTEMPTS;

  const {
    digits,
    fields,
    handleChange,
    handleKeyDown,
    handlePaste,
    registerRef,
    focusAt,
  } = useOtpInput({
    length: OTP_LENGTH,
    onChange: value => setFieldValue('code', value),
    onComplete: () => handleSubmit(),
  });

  const { formatted, remaining } = useChallengeCountdown({
    seconds: 180,
    onExpire: () => setCurrentStep(steps.password),
  });

  useEffect(() => {
    focusAt(0);
  }, [focusAt]);

  useEffect(() => {
    if (hasError && lastCountedSubmit.current !== submitCount) {
      lastCountedSubmit.current = submitCount;
      setInvalidCodeAttempts(attempts => attempts + 1);
    }
  }, [hasError, submitCount]);

  return (
    <Col>
      <Countdown $warn={remaining <= WARN_THRESHOLD}>
        <CountdownDot />
        {t('two-step.session-expires', { time: formatted })}
      </Countdown>
      <Title>{t('two-step.title')}</Title>
      <Subtitle>{t('two-step.subtitle')}</Subtitle>
      <Form noValidate onSubmit={handleSubmit}>
        <OtpRow $error={hasError}>
          {fields.map((field, index) => (
            <OtpCell
              key={field}
              ref={registerRef(index)}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              maxLength={1}
              value={digits[index]}
              $filled={!!digits[index]}
              $error={hasError}
              onChange={event => handleChange(index, event.target.value)}
              onKeyDown={event => handleKeyDown(index, event)}
              onPaste={handlePaste}
              aria-label={t('two-step.code-digit', { index: index + 1 })}
              autoFocus={index === 0}
            />
          ))}
        </OtpRow>
        <Feedback $error={hasError}>
          {hasError ? t('two-step.code-invalid') : t('two-step.code-hint')}
        </Feedback>
        {showCodeHelp && (
          <HelpBlock>
            <HelpTitle>{t('two-step.code-help-title')}</HelpTitle>
            <HelpText>{t('two-step.code-help-intro')}</HelpText>
            <TutorialSteps>
              {HELP_STEPS.map(step => (
                <TutorialStep key={step}>
                  <StepText>{t(`two-step.code-help-step${step}`)}</StepText>
                </TutorialStep>
              ))}
            </TutorialSteps>
          </HelpBlock>
        )}
        <ButtonSubmit
          block
          color="primary"
          type="submit"
          loading={isSubmitting ? 1 : 0}
        >
          {t(isSubmitting ? 'submitting' : 'two-step.submit')}
          {isSubmitting && <Spinner size={14} />}
        </ButtonSubmit>
      </Form>
      <ButtonLink color="link" onClick={toggleLostAccess}>
        {t('two-step.lost-access')}
      </ButtonLink>

      <LostAccessModal
        open={lostAccessOpen}
        toggle={toggleLostAccess}
        onBackToLogin={() => setCurrentStep(steps.email)}
      />
    </Col>
  );
};

export default LoginMfaChallenge;

LoginMfaChallenge.propTypes = {
  errors: propTypes.object.isRequired,
  submitCount: propTypes.number.isRequired,
  isSubmitting: propTypes.bool.isRequired,
  handleSubmit: propTypes.func.isRequired,
  setFieldValue: propTypes.func.isRequired,
  setCurrentStep: propTypes.func.isRequired,
  steps: propTypes.object.isRequired,
};
